import axios from 'axios';

// API Base URL - use relative URLs for same-domain deployment
// This avoids CORS issues and ensures session cookies work properly
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds
});

// CSRF Protection Removed: Not needed with header-based session authentication
// CSRF attacks only apply to cookie-based auth where browsers auto-send credentials
// Since we use x-session-id headers (not cookies), CSRF is impossible
export const fetchCsrfToken = async () => null; // No-op for backwards compatibility
export const clearCsrfToken = () => {}; // No-op for backwards compatibility

// Request interceptor - add session ID header
api.interceptors.request.use(
  (config) => {
    // SECURITY: Send session ID as header, not Authorization token
    const sessionId = localStorage.getItem('sessionId');
    if (sessionId) {
      config.headers['x-session-id'] = sessionId;
      // Store which sessionId was used for this request (for race condition protection)
      config._requestSessionId = sessionId;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors globally
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response) {
      // Handle 401 Unauthorized - session invalid
      if (error.response.status === 401) {
        const requestSessionId = error.config?._requestSessionId;
        const currentSessionId = localStorage.getItem('sessionId');

        console.log('[API] 401 received - request used sessionId:', requestSessionId?.substring(0, 8) + '...',
                     'current sessionId:', currentSessionId?.substring(0, 8) + '...',
                     'same?', requestSessionId === currentSessionId);

        // Grace period: don't auto-logout within 10 seconds of SSO login
        // This prevents race conditions where dashboard APIs fire before the session is ready
        const loginTimestamp = sessionStorage.getItem('ssoLoginTimestamp');
        const withinGracePeriod = loginTimestamp && (Date.now() - parseInt(loginTimestamp, 10)) < 10000;

        if (withinGracePeriod) {
          console.log('[API] 401 within SSO grace period - NOT clearing session (retryable)');
        } else if (!requestSessionId || requestSessionId === currentSessionId) {
          // Store current URL before redirecting to login
          const currentUrl = window.location.pathname + window.location.search + window.location.hash;
          // Only store if it's not already the login page or root
          if (currentUrl !== '/login' && currentUrl !== '/') {
            sessionStorage.setItem('redirectAfterLogin', currentUrl);
            console.log('[API] Storing redirect URL due to 401:', currentUrl);
          }

          // Clear session ID - AuthContext will handle logout
          localStorage.removeItem('sessionId');

          // Public pages (tracking, questionnaire, feedback, job listings) work
          // without a session: a stale session must not bounce a candidate who
          // followed an email link to the login page.
          const PUBLIC_PATHS = ['/track/', '/questionnaire/', '/interview-feedback/', '/jobs', '/careers', '/apply', '/verify'];
          const onPublicPage = PUBLIC_PATHS.some(p => currentUrl.startsWith(p));

          // Redirect to login if we're not already there
          if (currentUrl !== '/login' && !onPublicPage) {
            window.location.href = '/login';
          }
        } else {
          console.log('[API] 401 from old session - NOT clearing current session (new login occurred)');
        }
      }

      // Handle other errors (suppress expected auth errors)
      const errorCode = error.response.data?.code;
      const isExpectedAuthError = error.response.status === 401 || 
                                  (error.response.status === 500 && errorCode === 'VERIFICATION_ERROR');
      
      // Only log unexpected errors (suppress expected "not logged in" errors)
      if (!isExpectedAuthError) {
        console.error('API Error:', error.response.data);
      }
    } else if (error.request) {
      console.error('Network Error:', error.message);
    }

    return Promise.reject(error);
  }
);

// ========== AUTH API ==========
export const authAPI = {
  requestMagicLink: (email) =>
    api.post('/api/auth/request-magic-link', { email }),

  verifyMagicLink: (token) =>
    api.post('/api/auth/verify-magic-link', { token }),

  getCurrentUser: () =>
    api.get('/api/auth/me'),

  logout: () =>
    api.post('/api/auth/logout'),

  // User approval management (admin only)
  getPendingUsers: () =>
    api.get('/api/auth/pending-users'),

  approveUser: (userId, role) =>
    api.post('/api/auth/approve-user', { userId, action: 'approve', role }),

  rejectUser: (userId) =>
    api.post('/api/auth/approve-user', { userId, action: 'reject' }),

  reactivateUser: (userId) =>
    api.post('/api/auth/reactivate-user', { userId }),
};

// ========== CHALLENGES API ==========
export const challengesAPI = {
  getAll: (filters = {}) =>
    api.get('/api/challenges', { params: filters }),

  getById: (id) =>
    api.get(`/api/challenges/${id}`),

  create: (challengeData) =>
    api.post('/api/challenges', challengeData),

  update: (id, challengeData) =>
    api.put(`/api/challenges/${id}`, challengeData),

  updateFlags: (id, flags) =>
    api.put(`/api/challenges/${id}/flags`, { flags }),

  delete: (id) =>
    api.delete(`/api/challenges/${id}`),
};

// ========== INTERVIEWS API ==========
export const interviewsAPI = {
  getAll: () =>
    api.get('/api/interviews'),

  getById: (id) =>
    api.get(`/api/interviews/${id}`),

  create: (interviewData) =>
    api.post('/api/interviews', interviewData),

  update: (id, interviewData) =>
    api.put(`/api/interviews/${id}`, interviewData),

  delete: (id) =>
    api.delete(`/api/interviews/${id}`),

  addChallenge: (id, challengeId) =>
    api.post(`/api/interviews/${id}/challenges`, { challengeId }),

  removeChallenge: (id, challengeId) =>
    api.delete(`/api/interviews/${id}/challenges/${challengeId}`),
};

// ========== SESSIONS API ==========
// Create separate axios instance for challenges domain
// Use relative URLs for same-domain deployment
const CHALLENGES_BASE_URL = process.env.REACT_APP_CHALLENGES_URL || '';
const challengesApi = axios.create({
  baseURL: CHALLENGES_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

export const sessionsAPI = {
  create: (interviewId, candidateId, challengeId) =>
    api.post('/api/sessions/create', { interviewId, candidateId, challengeId }),

  get: (sessionId) =>
    api.get(`/api/sessions/${sessionId}`),

  getById: (sessionId) =>
    api.get(`/api/sessions/${sessionId}`),

  getStatus: (sessionId) =>
    challengesApi.get(`/${sessionId}/status`),

  submitFlag: (sessionId, flag) =>
    api.post(`/api/sessions/${sessionId}/flags`, { flag }),

  useHint: (sessionId, hintId) =>
    api.post(`/api/sessions/${sessionId}/hints`, { hintId }),

  complete: (sessionId) =>
    api.post(`/api/sessions/${sessionId}/complete`),

  terminate: (sessionId, reason) =>
    api.delete(`/api/sessions/${sessionId}`, { data: { reason } }),

  getActivity: (sessionId) =>
    api.get(`/api/sessions/${sessionId}/activity`),

  getTraffic: (sessionId) =>
    api.get(`/api/sessions/${sessionId}/traffic`),

  // Pause/resume session (for async assessments)
  pause: (sessionId) =>
    api.post(`/api/sessions/${sessionId}/pause`),

  resume: (sessionId) =>
    api.post(`/api/sessions/${sessionId}/resume`),
};

// ========== USERS API ==========
export const usersAPI = {
  getAll: () =>
    api.get('/api/users'),

  getById: (id) =>
    api.get(`/api/users/${id}`),

  create: (userData) =>
    api.post('/api/users', userData),

  update: (id, userData) =>
    api.put(`/api/users/${id}`, userData),

  delete: (id) =>
    api.delete(`/api/users/${id}`),
};

// ========== CANDIDATES API ==========
export const candidatesAPI = {
  getAll: () =>
    api.get('/api/candidates'),

  getById: (id) =>
    api.get(`/api/candidates/${id}`),

  create: (candidateData) =>
    api.post('/api/candidates', candidateData),

  update: (id, candidateData) =>
    api.put(`/api/candidates/${id}`, candidateData),

  getInterviews: (id) =>
    api.get(`/api/candidates/${id}/interviews`),
};

// ========== PERMISSIONS API ==========
export const permissionsAPI = {
  getAllPermissions: () =>
    api.get('/api/permissions/permissions'),

  getAllRoles: () =>
    api.get('/api/permissions/roles'),

  getRoleById: (id) =>
    api.get(`/api/permissions/roles/${id}`),

  updateRolePermissions: (id, permissions) =>
    api.put(`/api/permissions/roles/${id}/permissions`, { permissions }),

  createRole: (roleData) =>
    api.post('/api/permissions/roles', roleData),

  deleteRole: (id) =>
    api.delete(`/api/permissions/roles/${id}`),

  getMyPermissions: () =>
    api.get('/api/permissions/my-permissions'),

  updateUserPermissions: (userId, permissions) =>
    api.put(`/api/permissions/users/${userId}/permissions`, { permissions }),
};

// ========== ASSESSMENTS API ==========
// Recruiter-controlled assessments with full control over:
// - Hints configuration (enable/disable, penalties, approval)
// - Scheduling (live/async/timed modes)
// - Challenge selection (auto/manual)
// - Scoring configuration
export const assessmentsAPI = {
  // Create new assessment
  create: (assessmentData) =>
    api.post('/api/assessments', assessmentData),

  // List assessments (role-based filtering)
  // Query params: status, mode, page, limit
  getAll: (params = {}) =>
    api.get('/api/assessments', { params }),

  // Get assessment details by ID
  getById: (id) =>
    api.get(`/api/assessments/${id}`),

  // Update assessment (only draft/scheduled/active)
  update: (id, assessmentData) =>
    api.put(`/api/assessments/${id}`, assessmentData),

  // Send invitation email to candidate
  sendInvitation: (id) =>
    api.post(`/api/assessments/${id}/invite`),

  // Cancel assessment
  cancel: (id) =>
    api.delete(`/api/assessments/${id}`),

  // Get all assessments for a specific candidate
  getForCandidate: (candidateId, status = null) =>
    api.get(`/api/assessments/candidate/${candidateId}`, {
      params: status ? { status } : {}
    }),

  // Start an assessment (creates a challenge session)
  start: (id) =>
    api.post(`/api/assessments/${id}/start`),

  // Get assessment results (for completed assessments)
  getResults: (id) =>
    api.get(`/api/assessments/${id}/results`),

  // Multi-challenge progression endpoints
  // Get overall progress for multi-challenge assessment
  getProgress: (id) =>
    api.get(`/api/assessments/${id}/progress`),

  // Get current challenge for multi-challenge assessment
  getCurrentChallenge: (id) =>
    api.get(`/api/assessments/${id}/current-challenge`),

  // Complete current challenge and move to next
  completeChallenge: (id, challengeResults) =>
    api.post(`/api/assessments/${id}/complete-challenge`, challengeResults),
};

// ========== INTERVIEWERS API ==========
// Interviewer-specific functionality:
// - View assigned assessments
// - Monitor live sessions
// - Approve hints
// - Submit evaluations
export const interviewersAPI = {
  // Get all assessments assigned to current interviewer
  getAssignedAssessments: (params = {}) =>
    api.get('/api/interviewers/assessments', { params }),

  // Get detailed information about a specific assigned assessment
  getAssessmentDetails: (id) =>
    api.get(`/api/interviewers/assessments/${id}`),

  // Get all currently active sessions for assigned assessments
  getLiveSessions: () =>
    api.get('/api/interviewers/live-sessions'),

  // Approve or deny a hint request
  approveHint: (sessionId, approved, message = '') =>
    api.post(`/api/interviewers/hints/${sessionId}/approve`, {
      approved,
      message
    }),

  // Submit interviewer evaluation for a completed assessment
  submitEvaluation: (assessmentId, evaluationData) =>
    api.post(`/api/interviewers/evaluate/${assessmentId}`, evaluationData),

  // Get application details for an interview (read-only)
  getApplicationForInterview: (interviewId) =>
    api.get(`/api/interviewers/interviews/${interviewId}/application`),

  // Get performance statistics (Gamification #1)
  getStats: () =>
    api.get('/api/interviewers/stats'),

  // Get achievement badges (Gamification #2)
  getBadges: () =>
    api.get('/api/interviewers/badges')
};

// ========== ADMIN API ==========
// Admin-specific endpoints with full platform visibility:
// - Comprehensive dashboard with all metrics
// - All assessments across all recruiters
// - All sessions across all candidates
// - All users across all roles
// - Platform-wide statistics
export const adminAPI = {
  // Get comprehensive admin dashboard with platform metrics
  getDashboard: () =>
    api.get('/api/admin/dashboard'),

  // Get ALL assessments across all recruiters (with filtering)
  getAllAssessments: (params = {}) =>
    api.get('/api/admin/assessments', { params }),

  // Get ALL sessions across all candidates (with filtering)
  getAllSessions: (params = {}) =>
    api.get('/api/admin/sessions', { params }),

  // Get ALL users across all roles (with filtering)
  getAllUsers: (params = {}) =>
    api.get('/api/admin/users', { params }),

  // Get detailed platform statistics
  getStatistics: (params = {}) =>
    api.get('/api/admin/statistics', { params }),

  // FIX DC-32, DC-33: Data drift correction
  fixDataDrift: () =>
    api.post('/api/admin/fix-data-drift'),

  getDriftCorrectionStatus: () =>
    api.get('/api/admin/drift-correction-status'),
};

// ========== JOBS API ==========
// Job posting management for recruiters and admins
export const jobsAPI = {
  // Get candidate's own applications
  getMyApplications: () =>
    api.get('/api/jobs/my-applications'),

  // Get all job postings (requires manage_jobs permission)
  getAll: (params = {}) =>
    api.get('/api/jobs', { params }),

  // Get single job posting by ID
  getById: (id) =>
    api.get(`/api/jobs/${id}`),

  // Create new job posting
  create: (jobData) =>
    api.post('/api/jobs', jobData),

  // Update existing job posting
  update: (id, jobData) =>
    api.put(`/api/jobs/${id}`, jobData),

  // Publish job posting (make it active and visible)
  publish: (id) =>
    api.post(`/api/jobs/${id}/publish`),

  // Close job posting (stop accepting applications)
  close: (id) =>
    api.post(`/api/jobs/${id}/close`),

  // Delete job posting
  delete: (id) =>
    api.delete(`/api/jobs/${id}`),

  // Get all applications for a job
  getApplications: (id, params = {}) =>
    api.get(`/api/jobs/${id}/applications`, { params }),

  // Get single application details
  getApplication: (jobId, applicationId) =>
    api.get(`/api/jobs/${jobId}/applications/${applicationId}`),

  // Update application status
  updateApplicationStatus: (jobId, applicationId, status, reason = null, force = false) =>
    api.put(`/api/jobs/${jobId}/applications/${applicationId}/status`, { status, reason, force }),

  // Bulk update multiple application statuses (FIX: P1)
  bulkUpdateApplicationStatus: (jobId, applicationIds, status, reason) =>
    api.put(`/api/jobs/${jobId}/applications/bulk-update-status`, { applicationIds, status, reason }),

  // Add note to application
  addApplicationNote: (jobId, applicationId, note) =>
    api.post(`/api/jobs/${jobId}/applications/${applicationId}/notes`, { note }),

  // Mark candidate role fit decision (keep/move)
  submitRoleFitDecision: (jobId, applicationId, action, targetJobId = null, notes = '') =>
    api.post(`/api/jobs/${jobId}/applications/${applicationId}/role-fit-decision`, {
      action,
      targetJobId,
      notes
    }),

  // Update candidate profile info
  updateCandidateInfo: (jobId, applicationId, candidateData) =>
    api.put(`/api/jobs/${jobId}/applications/${applicationId}/candidate-info`, candidateData),

  // Talent Pool
  getTalentPool: (params = {}) =>
    api.get('/api/jobs/talent-pool', { params }),

  updateTalentPoolStatus: (id, status, notes) =>
    api.put(`/api/jobs/talent-pool/${id}/status`, { status, notes }),

  // Update rating (with categories)
  updateRating: (jobId, applicationId, ratings, overallRating) =>
    api.put(`/api/jobs/${jobId}/applications/${applicationId}/rating`, { ratings, overallRating }),

  // Update tags
  updateTags: (jobId, applicationId, tags) =>
    api.put(`/api/jobs/${jobId}/applications/${applicationId}/tags`, { tags }),

  // Toggle manager review flag
  toggleManagerReview: (jobId, applicationId, needsReview) =>
    api.put(`/api/jobs/${jobId}/applications/${applicationId}/manager-review`, { needsReview }),

  // Add internal comment
  addComment: (jobId, applicationId, comment, mentions, pinned) => {
    console.log('[API] addComment called with:', { jobId, applicationId, commentLength: comment?.length, mentions, pinned });
    return api.post(`/api/jobs/${jobId}/applications/${applicationId}/comments`, { comment, mentions, pinned });
  },

  // Send assessment to candidate
  sendAssessment: (jobId, applicationId, assessmentId) =>
    api.post(`/api/jobs/${jobId}/applications/${applicationId}/send-assessment`, { assessmentId }),

  // Download resume for an application (streams file or returns signed URL)
  downloadResume: (jobId, applicationId) =>
    api.get(`/api/jobs/${jobId}/applications/${applicationId}/resume`, { responseType: 'blob' }),

  // Upload or replace resume on an existing application
  uploadResume: (jobId, applicationId, file) => {
    const form = new FormData();
    form.append('resume', file);
    return api.put(`/api/jobs/${jobId}/applications/${applicationId}/resume`, form, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // Schedule interview for an application
  scheduleInterview: (jobId, applicationId, interviewData) =>
    api.post(`/api/jobs/${jobId}/applications/${applicationId}/schedule-interview`, interviewData),

  // Manually add a candidate sourced from LinkedIn, Naukri, etc.
  addCandidateManually: (jobId, candidateData) =>
    api.post(`/api/jobs/${jobId}/applications/manual`, candidateData),

  // Upload a candidate resume file (returns { fileUrl, filename })
  uploadCandidateResume: (formData) =>
    api.post('/api/jobs/upload-resume', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),

  // Schedule group interview (multiple candidates)
  scheduleGroupInterview: (jobId, groupInterviewData) =>
    api.post(`/api/jobs/${jobId}/schedule-group-interview`, groupInterviewData),

  // Get frequently used interviewers (personalized)
  getFrequentInterviewers: (limit = 10) =>
    api.get('/api/jobs/frequent-interviewers', { params: { limit } }),

  // Search users for autocomplete
  searchAzureADUsers: (query) =>
    api.get('/api/jobs/search-users', { params: { query } }),

  // Get interview details for an application
  getInterviewDetails: (jobId, applicationId) =>
    api.get(`/api/jobs/${jobId}/applications/${applicationId}/interview`),

  // Send manual interview reminder
  sendInterviewReminder: (interviewId) =>
    api.post(`/api/jobs/interviews/${interviewId}/send-reminder`),

  // Cancel interview
  cancelInterview: (interviewId, reason) =>
    api.post(`/api/jobs/interviews/${interviewId}/cancel`, { reason }),

  // Reschedule interview
  rescheduleInterview: (interviewId, rescheduleData) =>
    api.put(`/api/jobs/interviews/${interviewId}/reschedule`, rescheduleData),

  // Submit interview feedback (for interviewers)
  submitInterviewFeedback: (interviewId, feedbackData) =>
    api.put(`/api/jobs/interviews/${interviewId}/feedback`, feedbackData),

  // Assign assessment to candidate
  assignAssessment: (jobId, applicationId, assessmentData) =>
    api.post(`/api/jobs/${jobId}/applications/${applicationId}/assign-assessment`, assessmentData),

  // Get available assessments for a job (with profiling/recommendations)
  getAvailableAssessments: (jobId) =>
    api.get(`/api/jobs/${jobId}/available-assessments`),

  // Public endpoints (no auth)
  getPublicJobs: (params = {}) =>
    api.get('/api/jobs/public', { params }),

  getPublicJobByUuid: (uuid) =>
    api.get(`/api/jobs/public/${uuid}`),

  getPublicJobBySlug: (slugOrId) =>
    api.get(`/api/jobs/${slugOrId}/public`),

  // FIX BL-27: Interviewer schedule endpoints
  getStats: () =>
    api.get('/api/interviewers/stats'),

  // Get badges/achievements
  getBadges: () =>
    api.get('/api/interviewers/badges'),
  getMyInterviews: (params = {}) =>
    api.get('/api/jobs/my-interviews', { params }),

  // Get detailed interview information (for interviewers - includes assessment details, notes, timeline)
  getInterviewDetailsForInterviewer: (interviewId) =>
    api.get(`/api/jobs/interviews/${interviewId}/details`),

  downloadInterviewResume: (interviewId) =>
    api.get(`/api/jobs/interviews/${interviewId}/resume`),

  getMyInterviewStats: () =>
    api.get('/api/jobs/my-interviews/stats'),

  // Phone verification endpoints
  verifyPhone: (applicationId, code) =>
    api.post('/api/jobs/verify-phone', { applicationId, code }),

  resendPhoneCode: (applicationId) =>
    api.post('/api/jobs/resend-phone-code', { applicationId }),

  // Resend email verification
  resendVerification: (email, jobId, applicationId) =>
    api.post('/api/jobs/resend-verification', { email, jobId, applicationId }),

  // Public: Track application status by UUID
  getApplicationStatus: (uuid) =>
    api.get(`/api/jobs/application-status/${uuid}`),

  // Public: Withdraw application by UUID
  withdrawApplication: (uuid, reason) =>
    api.post(`/api/jobs/withdraw/${uuid}`, { reason }),

  // Public: ask for an automatically closed application to be reopened
  // (one-time token from the closure email)
  requestReopen: (uuid, token) =>
    api.post(`/api/jobs/application-status/${uuid}/reopen`, { token }),

  // Public: candidate adds missing profile details from the tracking page
  // (FormData: experienceYears, location, phone, resume file)
  updateApplicationProfile: (uuid, formData) =>
    api.put(`/api/jobs/application-status/${uuid}/profile`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),

  // Public: candidate's post-interview feedback (token from the follow-up email)
  getInterviewFeedbackContext: (interviewId, token) =>
    api.get(`/api/jobs/interview-feedback/${interviewId}`, { params: { t: token } }),
  submitCandidateInterviewFeedback: (interviewId, token, rating, comments) =>
    api.post(`/api/jobs/interview-feedback/${interviewId}`, { token, rating, comments }),

  // Per-application nudges: candidate reminder, remind-me, ping a teammate
  getApplicationPending: (jobId, applicationId) =>
    api.get(`/api/jobs/${jobId}/applications/${applicationId}/pending`),
  nudgeApplication: (jobId, applicationId, payload) =>
    api.post(`/api/jobs/${jobId}/applications/${applicationId}/nudge`, payload),
  getTeam: () =>
    api.get('/api/jobs/team'),

  // Pipeline health (recruiters and admins)
  getPipelineHealth: () =>
    api.get('/api/jobs/pipeline/health')
};

// ========== QUESTIONNAIRES API ==========
export const questionnairesAPI = {
  // Get all questionnaires
  getAll: (params = {}) =>
    api.get('/api/questionnaires', { params }),

  // Get single questionnaire
  getById: (id) =>
    api.get(`/api/questionnaires/${id}`),

  // Create questionnaire
  create: (questionnaireData) =>
    api.post('/api/questionnaires', questionnaireData),

  // Update questionnaire
  update: (id, questionnaireData) =>
    api.put(`/api/questionnaires/${id}`, questionnaireData),

  // Delete questionnaire
  delete: (id) =>
    api.delete(`/api/questionnaires/${id}`),

  // Send questionnaire to candidates, with optional extra per-candidate questions
  send: (id, applicationIds, extraQuestions) => {
    const payload = { applicationIds };
    if (extraQuestions && extraQuestions.length > 0) {
      payload.extraQuestions = extraQuestions;
    }
    return api.post(`/api/questionnaires/${id}/send`, payload);
  },

  // Get responses for a questionnaire
  getResponses: (id) =>
    api.get(`/api/questionnaires/${id}/responses`),

  // Get response by token (public)
  getResponseByToken: (token) =>
    api.get(`/api/questionnaires/responses/${token}`),

  // Submit response (public)
  submitResponse: (token, responses) =>
    api.post(`/api/questionnaires/responses/${token}`, { responses }),

  // Get responses for an application
  getApplicationResponses: (applicationId) =>
    api.get(`/api/questionnaires/responses/application/${applicationId}`),

  // Evaluate a response
  evaluateResponse: (responseId, evaluationData) =>
    api.put(`/api/questionnaires/responses/${responseId}/evaluate`, evaluationData)
};

// ========== PHONE SCREENINGS API ==========
export const phoneScreeningsAPI = {
  // Get all phone screenings
  getAll: (params = {}) =>
    api.get('/api/phone-screenings', { params }),

  // Get single phone screening
  getById: (id) =>
    api.get(`/api/phone-screenings/${id}`),

  // Schedule phone screening
  schedule: (screeningData) =>
    api.post('/api/phone-screenings', screeningData),

  // Complete phone screening
  complete: (id, completionData) =>
    api.put(`/api/phone-screenings/${id}/complete`, completionData),

  // Update phone screening
  update: (id, updateData) =>
    api.put(`/api/phone-screenings/${id}`, updateData),

  // Cancel phone screening
  cancel: (id) =>
    api.delete(`/api/phone-screenings/${id}`)
};

// ========== APPROVALS API ==========
export const approvalsAPI = {
  // Get all approvals
  getAll: (params = {}) =>
    api.get('/api/approvals', { params }),

  // Get pending approvals
  getPending: () =>
    api.get('/api/approvals/pending'),

  // Get single approval
  getById: (id) =>
    api.get(`/api/approvals/${id}`),

  // Request approval
  request: (approvalData) =>
    api.post('/api/approvals', approvalData),

  // Approve request
  approve: (id, responseNotes) =>
    api.put(`/api/approvals/${id}/approve`, { responseNotes }),

  // Reject request
  reject: (id, responseNotes) =>
    api.put(`/api/approvals/${id}/reject`, { responseNotes }),

  // Withdraw request
  withdraw: (id) =>
    api.delete(`/api/approvals/${id}`)
};

// ========== AVAILABILITY API ==========
export const availabilityAPI = {
  // Get interviewer availability
  getInterviewerAvailability: (interviewerId, params = {}) =>
    api.get(`/api/availability/interviewers/${interviewerId}`, { params }),

  // Update interviewer availability
  updateInterviewerAvailability: (interviewerId, availabilityData) =>
    api.put(`/api/availability/interviewers/${interviewerId}`, availabilityData),

  // Send availability request to candidate
  sendAvailabilityRequest: (requestData) =>
    api.post('/api/availability/request', requestData),

  // Get availability request by token (public)
  getAvailabilityRequest: (token) =>
    api.get(`/api/availability/requests/${token}`),

  // Select time slot (public)
  selectTimeSlot: (token, slotIndex) =>
    api.post(`/api/availability/requests/${token}/select`, { slotIndex }),

  // Get smart scheduling suggestions
  getSmartSuggestions: (params) =>
    api.get('/api/availability/smart-suggestions', { params })
};

// ========== TRAINING API (L&D Initiative) ==========
// Training paths, enrollments, sessions, teams, badges, leaderboards

export const trainingAPI = {
  // Training Paths
  getAllPaths: (params = {}) =>
    api.get('/api/training/paths', { params }),

  getPathById: (id) =>
    api.get(`/api/training/paths/${id}`),

  createPath: (pathData) =>
    api.post('/api/training/paths', pathData),

  updatePath: (id, pathData) =>
    api.put(`/api/training/paths/${id}`, pathData),

  publishPath: (id) =>
    api.post(`/api/training/paths/${id}/publish`),

  // Enrollments
  getMyEnrollments: (params = {}) =>
    api.get('/api/training/enrollments', { params }),

  enroll: (trainingPathId) =>
    api.post('/api/training/enrollments', { trainingPathId }),

  startEnrollment: (id) =>
    api.post(`/api/training/enrollments/${id}/start`),

  pauseEnrollment: (id) =>
    api.post(`/api/training/enrollments/${id}/pause`),

  resumeEnrollment: (id) =>
    api.post(`/api/training/enrollments/${id}/resume`),

  // Training Sessions
  createSession: (sessionData) =>
    api.post('/api/training/sessions', sessionData),

  startSession: (id) =>
    api.post(`/api/training/sessions/${id}/start`),

  submitFlag: (id, flag) =>
    api.post(`/api/training/sessions/${id}/submit-flag`, { flag }),

  useHint: (id, hintLevel) =>
    api.post(`/api/training/sessions/${id}/use-hint`, { hintLevel }),

  revealSolution: (id) =>
    api.post(`/api/training/sessions/${id}/reveal-solution`),

  retryChallenge: (id) =>
    api.post(`/api/training/sessions/${id}/retry`),

  // Team Challenges
  createTeam: (teamData) =>
    api.post('/api/training/teams', teamData),

  joinTeam: (id) =>
    api.post(`/api/training/teams/${id}/join`),

  leaveTeam: (id) =>
    api.post(`/api/training/teams/${id}/leave`),

  startTeam: (id) =>
    api.post(`/api/training/teams/${id}/start`),

  // Leaderboard
  getLeaderboard: (params = {}) =>
    api.get('/api/training/leaderboard', { params }),

  getMyLeaderboard: () =>
    api.get('/api/training/leaderboard/me'),

  // Badges
  getAllBadges: (params = {}) =>
    api.get('/api/training/badges', { params }),

  createBadge: (badgeData) =>
    api.post('/api/training/badges', badgeData),

  checkBadge: (id) =>
    api.get(`/api/training/badges/${id}/check`),
};

// ========== AD LABS API ==========
export const adLabsAPI = {
  listScenarios: () =>
    api.get('/api/labs/ad/scenarios'),
  start: (candidateId, assessmentId, interviewId, scenario, durationMinutes) =>
    api.post('/api/labs/ad/start', { candidateId, assessmentId, interviewId, scenario, durationMinutes }),
  stop: (instanceId) =>
    api.delete(`/api/labs/ad/${instanceId}/stop`),
  getStatus: (instanceId) =>
    api.get(`/api/labs/ad/${instanceId}/status`),
  getAccess: (instanceId) =>
    api.get(`/api/labs/ad/${instanceId}/access`),
  connect: (instanceId) =>
    api.get(`/api/labs/ad/${instanceId}/connect`),
  list: () =>
    api.get('/api/labs/ad'),
  myLab: (assessmentId) =>
    api.get('/api/labs/ad/my', { params: assessmentId ? { assessmentId } : {} }),
  submitFlag: (instanceId, flag) =>
    api.post(`/api/labs/ad/${instanceId}/flags`, { flag }),
  resetLab: (instanceId) =>
    api.post(`/api/labs/ad/${instanceId}/reset`),
  regenerateVpn: (instanceId) =>
    api.post(`/api/labs/ad/${instanceId}/vpn/regenerate`),
  downloadVpn: (instanceId) =>
    api.get(`/api/labs/ad/${instanceId}/vpn.ovpn`, { responseType: 'blob' }),
  // Share link management (recruiter)
  createShare: (instanceId) =>
    api.post(`/api/labs/ad/${instanceId}/share`),
  setShareEnabled: (instanceId, enabled) =>
    api.patch(`/api/labs/ad/${instanceId}/share`, { enabled }),
  // Share link access (public, no auth)
  getShare: (token) =>
    api.get(`/api/labs/ad/share/${token}`),
  getShareConnect: (token) =>
    api.get(`/api/labs/ad/share/${token}/connect`),
};

export const talentPoolAPI = {
  parse: (formData) =>
    api.post('/api/talent-pool/parse', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }),
  confirm: (candidates, source) =>
    api.post('/api/talent-pool/confirm', { candidates, source }),
  list: (params) =>
    api.get('/api/talent-pool', { params }),
  get: (id) =>
    api.get(`/api/talent-pool/${id}`),
  update: (id, data) =>
    api.put(`/api/talent-pool/${id}`, data),
  remove: (id) =>
    api.delete(`/api/talent-pool/${id}`),
  sendEmail: (payload) =>
    api.post('/api/talent-pool/email/send', payload),
};

export default api;
